import { Box, Flex, Grid, GridItem, Image, Input, Select, Text, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, FormControl, FormLabel, Button, Center, Spinner, Tooltip, IconButton, } from "@chakra-ui/react";
import MyDiv from "./degree.style";
import { ChevronDownIcon, EditIcon } from "@chakra-ui/icons";
import { AlertCard, CustomButton, CustomToast } from "../../../../components";
import { useEffect, useState } from "react";
import PlusIcon from "../../../../assets/images/Vector.svg";
import { useFetch } from "../../../../hooks/useFetch";
import { IDegree } from "./model";
import CustomTables, { Column, ILazyParams } from "../../../../components/CustomTable/CustomTables";

const Degree = () => {
  const [degrees, setDegrees] = useState<IDegree[]>([]);
  const [AlertOpen, setAlertOpen] = useState(false);
  const [DrawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pendingStatus, setPendingStatus] = useState<boolean | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentDegree, setCurrentDegree] = useState({ id: 0, degree: "", status: true, });
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const [statusChangeSource, setStatusChangeSource] = useState<"table" | "drawer" | null>(null);

  const [dataLoading, setDataLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [lazyParams, setLazyParams] = useState<ILazyParams>({
    first: 0, rows: 20, sortField: undefined, sortOrder: undefined,
    multiSortMeta: [], filters: {}, globalFilter: '',
  });

  useEffect(() => {
    loadDegree();
  }, [lazyParams]);

  const loadDegree = async () => {
    setDataLoading(true);
    const params = new URLSearchParams();
    params.set('first', String(lazyParams.first));
    params.set('rows', String(lazyParams.rows));
    if (lazyParams.sortField) params.set('sortField', lazyParams.sortField);
    if (lazyParams.sortOrder) params.set('sortOrder', String(lazyParams.sortOrder));
    if (lazyParams.globalFilter) params.set('globalFilter', lazyParams.globalFilter);
    if (lazyParams.filters && Object.keys(lazyParams.filters).length) {
      params.set('filters', JSON.stringify(lazyParams.filters));
    }

    const response = await fetchApi<any>(`Hrlense_Degree?${params.toString()}`, "GET");
    if (response) {
      setDegrees(response.data ?? response ?? []);
      setTotalCount(response.totalRecords ?? response.length ?? 0);
    }
    setDataLoading(false);
  };

  const handleStatusChange = (id: number, newStatus: boolean) => {
    setSelectedId(id);
    setPendingStatus(newStatus);
    setStatusChangeSource("table");
    setAlertOpen(true);
  };

  const handleDrawerStatusChange = (newStatus: boolean) => {
    setPendingStatus(newStatus);
    setStatusChangeSource("drawer");
    setAlertOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (selectedId !== null && pendingStatus !== null) {
      const scoreId = statusChangeSource === "table" ? selectedId : currentDegree.id;

      const response = await fetchApi(`Hrlense_Degree/toggleStatus/${scoreId}`, "PUT", { id: scoreId, status: pendingStatus });
      if (response) {
        if (statusChangeSource === "table") {
          await loadDegree();
        } else {
          setCurrentDegree((prev) => ({ ...prev, status: pendingStatus }));
        }
      }
    }
    setAlertOpen(false);
  };

  const handleAddNewDegree = () => {
    setIsEditMode(false);
    setCurrentDegree({ id: 0, degree: "", status: true, });
    setDrawerOpen(true);
  };

  const handleEditDegree = (branch: IDegree) => {
    setIsEditMode(true);
    setSelectedId(branch.id);
    setCurrentDegree(branch);
    setDrawerOpen(true);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    if (!/^[A-Za-z\s]*$/.test(value)) {
      return;
    }
    setCurrentDegree(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleSubmit = async () => {
    if (!currentDegree.degree.trim()) {
      addToast({ message: "Please enter Degree", status: "warning" });
      return;
    }
    if (isEditMode) {
      const response = await fetchApi(`Hrlense_Degree/${currentDegree.id}`, 'PUT', { id: currentDegree.id, degree: currentDegree.degree, status: currentDegree.status }, null, "Degree updated successfully");
      if (response) await loadDegree();
    } else {
      const response = await fetchApi<IDegree>('Hrlense_Degree', 'POST', { degree: currentDegree.degree, status: currentDegree.status }, null, "Degree added successfully");
      if (response) await loadDegree();
    }
    setDrawerOpen(false);
  };

  const getStatusColor = (status: boolean) => (status ? "#38A169" : "#E53E3E");

  return (
    <MyDiv>
      <Box position="relative" minH="300px">
        <>
          <Flex className="page_heading flex_header">
            <Text className="font-poppins text_semibold text_3xl">Degree List</Text>
            <Flex gap={2}>
              <CustomButton title="Add New Degree" className=" btn font-poppins text_sm text_medium" rightIcon={<Image src={PlusIcon} className="logo" />} onClick={handleAddNewDegree} />
            </Flex>
          </Flex>
          <Grid className="grid_container">
            <GridItem colSpan={12}>
              <CustomTables<IDegree>
                value={degrees} loading={dataLoading} emptyMessage="No degrees found." preferenceKey="degreeTable" showGlobalSearch={false} showToolbar={true} lazy
                lazyParams={lazyParams} onLazyParamsChange={setLazyParams} totalRecords={totalCount} showPagination rowsPerPageOptions={[20, 100, 200, 500]} maxTableHeight={null}
              >
                <Column header="S. No" body={(_, index) => lazyParams.first + index + 1} />
                <Column header="Degree" field="degree" sortable filterable filterType="text" />
                <Column header="Status" field="status" filterable filterType="boolean"
                  body={(res: any) => (
                    <Select
                      value={res.status ? "true" : "false"}
                      onChange={(e) => handleStatusChange(res.id, e.target.value === "true")}
                      width="120px" height="32px" icon={<ChevronDownIcon />} borderRadius="4px"
                      color={getStatusColor(res.status)} className="font-poppins text_medium text_xsm"
                    >
                      <option value="true" style={{ color: "#38A169" }}>Active</option>
                      <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
                    </Select>
                  )}
                />
                <Column header="Actions" frozen="right" body={(res: any) => (
                    <Tooltip label="Edit Degree" hasArrow>
                      <IconButton aria-label="Edit" icon={<EditIcon />} size="sm" colorScheme="orange" variant="ghost" onClick={() => handleEditDegree(res)} />
                    </Tooltip>
                  )}
                />
              </CustomTables>
            </GridItem>
          </Grid>
          <AlertCard isOpen={AlertOpen} onClose={() => setAlertOpen(false)} onConfirm={handleConfirmStatusChange} />
          <Drawer isOpen={DrawerOpen} placement="right" onClose={() => setDrawerOpen(false)} size="sm">
            <DrawerOverlay />
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader borderBottomWidth="1px">{isEditMode ? "Edit Degree" : "Add New Degree"}</DrawerHeader>
              <DrawerBody display="flex" flexDirection="column" justifyContent="space-between" height="100%" >
                <Box py={4}>
                  <FormControl mb={4} isRequired>
                    <FormLabel>Degree</FormLabel>
                    <Input name="degree" value={currentDegree.degree} onChange={handleInputChange} placeholder="Enter Degree" />
                  </FormControl>
                  <FormControl mb={4}>
                    <FormLabel>Status</FormLabel>
                    <Select w="120px" value={currentDegree.status ? "true" : "false"} onChange={(e) => handleDrawerStatusChange(e.target.value === "true")} color={getStatusColor(currentDegree.status)}>
                      <option value="true" style={{ color: "#38A169" }}> Active</option>
                      <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
                    </Select>
                  </FormControl>
                </Box>
                <Flex justify="flex-end" mt={4} gap={2}>
                  <Button className="font-poppins text_sm text_medium" onClick={() => setDrawerOpen(false)} style={{ backgroundColor: "#D92F34", color: "white" }} >Cancel</Button>
                  <Button onClick={handleSubmit} className="font-poppins text_sm text_medium" style={{ backgroundColor: "#006838", color: "white" }} >{isEditMode ? "Update Degree" : "Add New Degree"}</Button>
                </Flex>
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        </>
      </Box>
    </MyDiv>
  );
};
export default Degree;